import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Auth helper functions
export const auth = {
  // Sign up with email and password
  signUp: async (email, password, userData = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
    return { data, error }
  },

  // Sign in with email and password
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Get current user
  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  // Listen to auth state changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Database helper functions
export const db = {
  // Users table operations
  users: {
    // Create user profile
    create: async (userData) => {
      const { data, error } = await supabase
        .from('users')
        .insert([userData])
        .select()
        .single()
      return { data, error }
    },

    // Get user by ID
    getById: async (userId) => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()
      return { data, error }
    },

    // Update user profile
    update: async (userId, updates) => {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()
      return { data, error }
    }
  },

  // Registrations table operations
  registrations: {
    // Create registration
    create: async (registrationData) => {
      const { data, error } = await supabase
        .from('registrations')
        .insert([registrationData])
        .select()
        .single()
      return { data, error }
    },

    // Get registrations by user
    getByUser: async (userId) => {
      const { data, error } = await supabase
        .from('registrations')
        .select(`
          *,
          retreats (
            title,
            date,
            location,
            price
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
      return { data, error }
    },

    // Get registration by ID
    getById: async (registrationId) => {
      const { data, error } = await supabase
        .from('registrations')
        .select(`
          *,
          retreats (
            title,
            date,
            location,
            price
          ),
          users (
            first_name,
            last_name,
            email
          )
        `)
        .eq('id', registrationId)
        .single()
      return { data, error }
    },

    // Update registration status
    updateStatus: async (registrationId, status) => {
      const { data, error } = await supabase
        .from('registrations')
        .update({ status })
        .eq('id', registrationId)
        .select()
        .single()
      return { data, error }
    }
  },

  // Payments table operations
  payments: {
    // Create payment record
    create: async (paymentData) => {
      const { data, error } = await supabase
        .from('payments')
        .insert([paymentData])
        .select()
        .single()
      return { data, error }
    },

    // Get payments by registration
    getByRegistration: async (registrationId) => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('registration_id', registrationId)
        .order('created_at', { ascending: false })
      return { data, error }
    },

    // Update payment status
    updateStatus: async (paymentId, status, stripePaymentIntentId = null) => {
      const updates = { status }
      if (stripePaymentIntentId) {
        updates.stripe_payment_intent_id = stripePaymentIntentId
      }
      
      const { data, error } = await supabase
        .from('payments')
        .update(updates)
        .eq('id', paymentId)
        .select()
        .single()
      return { data, error }
    }
  },

  // Retreats table operations
  retreats: {
    // Get all retreats
    getAll: async () => {
      const { data, error } = await supabase
        .from('retreats')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: true })
      return { data, error }
    },

    // Get retreat by ID
    getById: async (retreatId) => {
      const { data, error } = await supabase
        .from('retreats')
        .select('*')
        .eq('id', retreatId)
        .single()
      return { data, error }
    },

    // Update available spots
    updateSpots: async (retreatId, spotsLeft) => {
      const { data, error } = await supabase
        .from('retreats')
        .update({ spots_left: spotsLeft })
        .eq('id', retreatId)
        .select()
        .single()
      return { data, error }
    }
  }
}

export default supabase
